import {ErrorCode} from '@sollapay/enums';
import {
  Button,
  FieldInput,
  FormSectionHeading,
  Icon,
  Modal,
  ModalClose
} from '@sollapay/ui/components';
import {InviteMemberIllustration} from '@sollapay/ui/illustrations';
import {Controller} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {emailRule} from '@/lib/validation';
import {getErrorCode} from '@/utils';

import {RolePickerRadioGroup} from './RolePickerRadioGroup';
import {useInviteMemberForm} from './useInviteMemberForm';
import {useRolesQuery} from '../../hooks';

import type {FC} from 'react';

export interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const InviteMemberModal: FC<InviteMemberModalProps> = ({isOpen, onClose, onSuccess}) => {
  const {t} = useTranslation(['team', 'common']);
  const {data: roles = []} = useRolesQuery();
  const {form, handleSend, isSending, sendError} = useInviteMemberForm({isOpen, onSuccess});
  const {
    register,
    control,
    formState: {errors, isDirty}
  } = form;

  const emailError =
    getErrorCode(sendError) === ErrorCode.MEMBER_ALREADY_EXISTS
      ? t('common:fields.email.alreadyExists')
      : errors.email?.message;

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDirty={isDirty}
      illustration={<InviteMemberIllustration />}
      title={t('inviteModal.title')}
      description={t('inviteModal.description')}
      footer={
        <div className="flex w-full items-center justify-end gap-3">
          <ModalClose render={<Button variant="secondary" size="sm" disabled={isSending} />}>
            {t('common:cancel')}
          </ModalClose>
          <Button
            variant="primary"
            size="sm"
            isLoading={isSending}
            icon={<Icon name="chevron-left" />}
            iconPosition="end"
            onClick={() => void handleSend()}
          >
            {t('inviteModal.send')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        <FieldInput
          label={t('inviteModal.emailLabel')}
          placeholder={t('inviteModal.emailPlaceholder')}
          required
          type="email"
          error={emailError}
          startAdornment={<Icon name="mail" className="size-4" />}
          {...register('email', emailRule(t))}
        />
        <FormSectionHeading
          title={t('inviteModal.roleLabel')}
          description={t('inviteModal.roleDescription')}
          titleSize="sm"
        />
        <Controller
          name="roleId"
          control={control}
          rules={{required: t('common:fields.role.required')}}
          render={({field}) => (
            <RolePickerRadioGroup
              roles={roles}
              value={field.value}
              onChange={field.onChange}
              error={errors.roleId?.message}
            />
          )}
        />
      </div>
    </Modal>
  );
};
